import type { LucideIcon } from 'lucide-react';
import { ChevronRight, ChevronsUpDown } from 'lucide-react';
import { type ButtonHTMLAttributes, type ReactNode, useId } from 'react';
import { cx } from './cx';

/** A section of a page: title, description, actions, content and an optional footer. */
export function Panel({
  title,
  description,
  actions,
  footer,
  flush,
  className,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  /** Content edge to edge, for tables. */
  flush?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const headingId = useId();
  const head = title != null || description != null || actions != null;
  return (
    <section
      className={cx('mz-panel', className)}
      aria-labelledby={title != null ? headingId : undefined}
    >
      {head && (
        <header className="mz-panel-head">
          <div>
            {title != null && (
              <h2 className="mz-panel-title" id={headingId}>
                {title}
              </h2>
            )}
            {description != null && <p className="mz-panel-desc">{description}</p>}
          </div>
          {actions != null && <div className="mz-row">{actions}</div>}
        </header>
      )}
      {children != null && (
        <div className={cx('mz-panel-body', flush && 'mz-panel-body-flush')}>{children}</div>
      )}
      {footer != null && <footer className="mz-panel-foot">{footer}</footer>}
    </section>
  );
}

/** The top of every page: title, meta line, actions and, for something with sub-pages, tabs. */
export function PageHeader({
  title,
  meta,
  description,
  actions,
  leading,
  tabs,
}: {
  title: ReactNode;
  meta?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Before the title: an avatar or a project mark. */
  leading?: ReactNode;
  tabs?: ReactNode;
}) {
  return (
    <div className="mz-pagehead">
      <div className="mz-pagehead-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          {leading}
          <div className="mz-pagehead-titles">
            <h1 className="mz-pagehead-title">{title}</h1>
            {meta != null && <div className="mz-pagehead-meta">{meta}</div>}
            {description != null && <p className="mz-pagehead-desc">{description}</p>}
          </div>
        </div>
        {actions != null && <div className="mz-pagehead-actions">{actions}</div>}
      </div>
      {tabs}
    </div>
  );
}

/**
 * Underlined links between the pages of one thing. Put links with `className="mz-tab"`
 * inside; a router's NavLink marks the current one with aria-current="page".
 */
export function Tabs({ label, children }: { label: string; children: ReactNode }) {
  return (
    <nav className="mz-tabs" aria-label={label}>
      {children}
    </nav>
  );
}

export function TabCount({ children }: { children: ReactNode }) {
  return <span className="mz-tab-count">{children}</span>;
}

/** Switches between two to four views of the same page, such as Test and Live data. */
export function SegmentedControl<Id extends string>({
  label,
  items,
  value,
  onChange,
  className,
}: {
  label: string;
  items: readonly { id: Id; label: ReactNode }[];
  value: Id;
  onChange: (id: Id) => void;
  className?: string;
}) {
  return (
    <fieldset className={cx('mz-seg', className)}>
      <legend className="mz-sr-only">{label}</legend>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-pressed={item.id === value}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </fieldset>
  );
}

/** The bar above every page: breadcrumbs on the left, quick actions on the right. */
export function TopBar({
  menuButton,
  breadcrumbs,
  actions,
}: {
  /** Phones: the button that opens the sidebar. */
  menuButton?: ReactNode;
  breadcrumbs: ReactNode[];
  actions?: ReactNode;
}) {
  return (
    <header className="mz-topbar">
      {menuButton}
      <ol className="mz-crumbs" aria-label="Breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: crumbs are a fixed path
          <li key={i}>
            {i > 0 && (
              <ChevronRight size={14} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
            )}
            {crumb}
          </li>
        ))}
      </ol>
      {actions != null && <div className="mz-topbar-actions">{actions}</div>}
    </header>
  );
}

/** The dashboard's sidebar: brand, optional switcher, navigation groups and the person. */
export function Sidebar({
  brand,
  switcher,
  footer,
  children,
  id,
}: {
  brand: ReactNode;
  switcher?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <aside className="mz-sidebar" id={id} aria-label="Dashboard">
      {brand}
      <div className="mz-sidebar-scroll">
        {switcher}
        {children}
      </div>
      {footer != null && <div className="mz-sidebar-foot">{footer}</div>}
    </aside>
  );
}

export function NavGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <nav className="mz-nav-group" aria-label={label ?? 'Main'}>
      {label && (
        <div className="mz-nav-label" aria-hidden="true">
          {label.toUpperCase()}
        </div>
      )}
      {children}
    </nav>
  );
}

/** What goes inside a sidebar link (`className="mz-nav-item"`): icon, label, badge. */
export function NavItemContent({
  icon: Icon,
  badge,
  children,
}: {
  icon?: LucideIcon;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />}
      <span>{children}</span>
      {badge != null && <span className="mz-nav-item-badge">{badge}</span>}
    </>
  );
}

/** The current project, opening a list to switch to another. */
export function SwitcherButton({
  name,
  ...button
}: { name: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="mz-switcher"
      aria-label={`Switch project: ${name}`}
      {...button}
    >
      <span className="mz-switcher-mark" aria-hidden="true">
        {initials(name).slice(0, 1)}
      </span>
      <span className="mz-switcher-name">{name}</span>
      <ChevronsUpDown size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
    </button>
  );
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

/** A person's picture, or their initials. */
export function Avatar({
  name,
  src,
  size = 28,
  className,
}: {
  name: string;
  src?: string | null;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cx('mz-avatar', className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" /> : initials(name)}
    </span>
  );
}

/** The signed-in person at the bottom of the sidebar; opens the account menu. */
export function UserButton({
  name,
  email,
  ...button
}: { name: string; email: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="mz-user" aria-haspopup="menu" {...button}>
      <Avatar name={name} />
      <span className="mz-user-text">
        <span className="mz-user-name">{name}</span>
        <span className="mz-user-email">{email}</span>
      </span>
      <ChevronsUpDown size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
    </button>
  );
}
